/**
 * 사이드바 계정 섹션 컴포넌트 - Hero UI 버전
 * 순환 참조 방지를 위해 UserProfileComponent를 props로 주입받음
 */ import React from 'react';
import { Button, Avatar, Tooltip, cn } from '@heroui/react';
import { motion, AnimatePresence } from 'framer-motion';
import * as m from "@paraglide/messages";
import { LogIn, LogOut, Sparkles } from 'lucide-react';
export const AccountSectionHeroUI = ({ isAuthenticated, userName, userAvatar, onLogout, onLoginClick, isTabletView, isMobileSidebar, isIconOnly, userProfileComponent })=>{
    const compactMode = typeof isIconOnly === 'boolean' ? isIconOnly && !isMobileSidebar : isTabletView && !isMobileSidebar;
    const buttonVariants = {
        initial: {
            scale: 0.9,
            opacity: 0
        },
        animate: {
            scale: 1,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 400,
                damping: 25
            }
        },
        hover: {
            y: -2,
            transition: {
                type: "spring",
                stiffness: 400,
                damping: 20
            }
        },
        tap: {
            scale: 0.95
        }
    };
    const iconVariants = {
        initial: {
            rotate: 0
        },
        hover: {
            rotate: [
                0,
                -10,
                10,
                -10,
                0
            ],
            transition: {
                duration: 0.5,
                times: [
                    0,
                    0.25,
                    0.5,
                    0.75,
                    1
                ]
            }
        }
    };
    const sparkleVariants = {
        initial: {
            opacity: 0,
            scale: 0
        },
        hover: {
            opacity: [
                0,
                1,
                0
            ],
            scale: [
                0,
                1.2,
                0
            ],
            rotate: 360,
            transition: {
                duration: 1.5,
                repeat: Infinity,
                repeatDelay: 0.5
            }
        }
    };
    const handleLogout = async ()=>{
        if (!onLogout) {
            return;
        }
        try {
            await onLogout();
        } catch (error) {
            console.error('[AccountSectionHeroUI] Logout failed:', error);
        }
    };
    // 로그인 상태에서 외부에서 주입받은 UserProfile 컴포넌트 사용
    // 태블릿 뷰에서도 userProfileComponent가 있으면 무조건 표시
    if (isAuthenticated && userProfileComponent) {
        return /*#__PURE__*/ React.createElement("div", {
            className: cn('mt-auto w-full'),
            style: {
                display: 'flex',
                justifyContent: 'center'
            }
        }, /*#__PURE__*/ React.createElement("div", {
            className: cn('w-full flex justify-center'),
            style: {
                position: 'relative',
                transform: compactMode ? 'scale(0.85)' : undefined,
                transformOrigin: 'top center'
            },
            "data-testid": "sidebar-user-profile-preview"
        }, userProfileComponent));
    }
    // 로그인 상태이지만 userProfileComponent가 없는 경우 - Hero UI 스타일 프로필
    if (isAuthenticated && !userProfileComponent && userName) {
        // 태블릿 뷰에서는 간단한 아바타와 로그아웃 버튼
        if (compactMode) {
            return /*#__PURE__*/ React.createElement("div", {
                className: "mt-auto w-full px-2 pt-3 flex justify-center"
            }, /*#__PURE__*/ React.createElement("div", {
                className: "w-full flex flex-col items-center gap-2"
            }, /*#__PURE__*/ React.createElement(Avatar, {
                src: userAvatar,
                name: userName,
                size: "md",
                color: "primary",
                isBordered: true,
                className: "transition-all hover:scale-105"
            }), /*#__PURE__*/ React.createElement(Button, {
                size: "sm",
                color: "danger",
                variant: "light",
                isIconOnly: true,
                onPress: handleLogout,
                className: "mt-1"
            }, /*#__PURE__*/ React.createElement(LogOut, {
                size: 16
            }))));
        }
        return /*#__PURE__*/ React.createElement("div", {
            className: cn("mt-auto w-full pt-4 px-3", "border-t border-default-200")
        }, /*#__PURE__*/ React.createElement("div", {
            className: cn("flex items-center gap-3", isMobileSidebar ? "justify-start" : "justify-end")
        }, isMobileSidebar && /*#__PURE__*/ React.createElement(Avatar, {
            src: userAvatar,
            name: userName,
            size: "md",
            color: "primary",
            isBordered: true,
            className: "transition-all hover:scale-110"
        }), /*#__PURE__*/ React.createElement("div", {
            className: cn("flex flex-col", isMobileSidebar ? "flex-1" : "items-end")
        }, /*#__PURE__*/ React.createElement("span", {
            className: "text-sm font-semibold"
        }, userName), /*#__PURE__*/ React.createElement(Button, {
            size: "sm",
            color: "danger",
            variant: "light",
            startContent: /*#__PURE__*/ React.createElement(LogOut, {
                size: 16
            }),
            onPress: handleLogout,
            className: "mt-1"
        }, m.common_logout())), !isMobileSidebar && /*#__PURE__*/ React.createElement(Avatar, {
            src: userAvatar,
            name: userName,
            size: "md",
            color: "primary",
            isBordered: true,
            className: "transition-all hover:scale-110"
        })));
    }
    const loginContent = /*#__PURE__*/ React.createElement(Tooltip, {
        content: "계정에 로그인하여 모든 기능을 이용하세요",
        placement: isMobileSidebar || compactMode ? "right" : "top",
        color: "primary",
        delay: 400
    }, /*#__PURE__*/ React.createElement(Button, {
        color: "primary",
        variant: "shadow",
        size: compactMode ? "md" : "lg",
        isIconOnly: compactMode,
        onPress: onLoginClick,
        className: cn(compactMode ? "w-auto h-12 rounded-full shadow-lg shadow-primary/40" : "font-semibold w-full bg-gradient-to-br from-primary-400 to-primary-600 shadow-lg shadow-primary/40 hover:shadow-xl hover:shadow-primary/50 transition-all duration-200 justify-center")
    }, /*#__PURE__*/ React.createElement(motion.div, {
        variants: iconVariants,
        className: "relative flex items-center justify-center"
    }, /*#__PURE__*/ React.createElement(LogIn, {
        size: compactMode ? 22 : 20
    }), /*#__PURE__*/ React.createElement(motion.div, {
        variants: sparkleVariants,
        className: "absolute -top-1 -right-1"
    }, /*#__PURE__*/ React.createElement(Sparkles, {
        size: 10,
        className: "text-white/80"
    }))), !compactMode && m.common_login()));
    return /*#__PURE__*/ React.createElement("div", {
        className: cn("w-full px-3", isMobileSidebar ? "pt-8 pb-8" : "pt-4 pb-6", "flex flex-col items-stretch gap-3")
    }, /*#__PURE__*/ React.createElement(motion.div, {
        variants: buttonVariants,
        initial: "initial",
        animate: "animate",
        whileHover: "hover",
        whileTap: "tap",
        className: cn("w-full", compactMode ? "flex justify-center" : "")
    }, loginContent), !isTabletView && /*#__PURE__*/ React.createElement(AnimatePresence, null, /*#__PURE__*/ React.createElement(motion.p, {
        initial: {
            opacity: 0,
            y: -10
        },
        animate: {
            opacity: 0.6,
            y: 0
        },
        exit: {
            opacity: 0,
            y: -10
        },
        transition: {
            delay: 0.25
        },
        className: cn("text-xs text-default-500", isMobileSidebar ? "text-left" : "text-right")
    }, "로그인하여 더 많은 기능을 이용하세요")));
};
export default AccountSectionHeroUI;
